/**
 * The running stream as the whole app sees it: the live bar, Home and the
 * title bar read this instead of the host page, so they stay right when the host
 * page is not open or the webview was reloaded.
 *
 * The native side keeps the session and `activeStream()` describes it, which is
 * also what the host page restores from. This just asks again every few seconds
 * (and on demand when the host page starts or stops a stream), so there is only
 * one source of truth and nothing to keep in sync by hand.
 */

import { isDesktopApp } from './platform'
import { friends } from './friends.svelte'
import { activeStream, listWindows, type HostEvent } from './tauri'

export interface LiveRoom {
  code: string
  shareUrl: string
  /** When the stream started, in `Date.now()` terms. */
  startedAt: number
  hwnd: number | null
  monitor: number | null
  /** What is being shared, for the bar. Empty until the window list answers. */
  title: string
  viewers: number
}

export const live = $state<{ room: LiveRoom | null }>({ room: null })

const POLL_MS = 3000

/** Viewers currently connected, from the events the native side retains. */
export function connectedViewers(events: HostEvent[]): number {
  let connected: string[] = []
  for (const event of events) {
    if (event.type === 'viewer_connected' && !connected.includes(event.peer_id))
      connected.push(event.peer_id)
    else if (event.type === 'viewer_disconnected' || event.type === 'viewer_left')
      connected = connected.filter((id) => id !== event.peer_id)
  }
  return connected.length
}

let titles = new Map<number, string>()

async function titleOf(hwnd: number | null, monitor: number | null): Promise<string> {
  if (hwnd === null) return monitor === null ? '' : `Screen ${monitor}`
  const known = titles.get(hwnd)
  if (known !== undefined) return known
  try {
    titles = new Map((await listWindows()).map((w) => [w.hwnd, w.title || w.exe]))
  } catch {
    // The bar works without a name.
  }
  return titles.get(hwnd) ?? ''
}

/** Ask the native side what is running now. */
export async function refreshLive(): Promise<void> {
  if (!isDesktopApp) return
  try {
    const state = await activeStream()
    // Publish from the native snapshot even when the host page is not mounted.
    // Reconnects then replay the current tunnel URL rather than a page's old one.
    friends.setPresence(
      state
        ? { state: 'sharing', code: state.summary.room_code, share_url: state.summary.share_url }
        : { state: 'online' },
    )
    if (!state) {
      live.room = null
      return
    }
    const hwnd = state.hwnd || null
    live.room = {
      code: state.summary.room_code,
      shareUrl: state.summary.share_url,
      startedAt: Date.now() - state.elapsed_ms,
      hwnd,
      monitor: state.monitor_index,
      title: await titleOf(hwnd, state.monitor_index),
      viewers: connectedViewers(state.events),
    }
  } catch {
    // Keep what we last knew; the next poll tries again.
  }
}

/** Start following the stream. Returns the stop function. */
export function watchLive(): () => void {
  if (!isDesktopApp) return () => {}
  void refreshLive()
  const timer = setInterval(() => void refreshLive(), POLL_MS)
  return () => clearInterval(timer)
}
